import { MercatorCoordinate } from 'maplibre-gl';
import type { MapView } from '../map/model';
import type { CompositionInsetMap } from './model';
import type { RenderScene } from '../map/renderingAdapter';

export type GroundFootprint = readonly (readonly [number, number])[];

export { createDefaultInsetMap } from './insetDefaults';

/** Omit hidden content entirely; main-map visibility is deliberately independent. Stale IDs are inert. */
export function insetOverlayScene(
  scene: RenderScene,
  inset: Pick<
    CompositionInsetMap,
    'hiddenLayerIds' | 'hiddenAnnotationLayerIds' | 'hiddenAnnotationIds'
  >,
): RenderScene {
  const hiddenLayers = new Set(inset.hiddenLayerIds);
  const hiddenAnnotationLayers = new Set(inset.hiddenAnnotationLayerIds);
  const hiddenAnnotations = new Set(inset.hiddenAnnotationIds);
  return {
    ...scene,
    layers: scene.layers
      .filter((layer) => !hiddenLayers.has(layer.id))
      .map((layer) => ({ ...layer, visible: true })),
    annotationLayers: (scene.annotationLayers ?? [])
      .filter((layer) => !hiddenAnnotationLayers.has(layer.id))
      .map((layer) => ({
        ...layer,
        visible: true,
        annotations: layer.annotations
          .filter((annotation) => !hiddenAnnotations.has(annotation.id))
          .map((annotation) => ({ ...annotation, visible: true })),
      })),
  };
}

/** Logical camera ratio, independent of export backing pixel ratio. Never enlarge overlays. */
export function insetPresentationScale(mainZoom: number, insetZoom: number): number {
  if (!Number.isFinite(mainZoom) || !Number.isFinite(insetZoom)) return 1;
  return Math.max(1 / 64, Math.min(1, 2 ** Math.min(0, insetZoom - mainZoom)));
}

/** Actual logical viewport corners on the existing 2D ground plane; never a bbox substitute. */
export function deriveGroundFootprint(
  width: number,
  height: number,
  centerLongitude: number,
  unproject: (point: readonly [number, number]) => { lng: number; lat: number },
): GroundFootprint | null {
  if (![width, height, centerLongitude].every(Number.isFinite) || width <= 0 || height <= 0)
    return null;
  try {
    const corners = [
      [0, 0],
      [width, 0],
      [width, height],
      [0, height],
    ] as const;
    const result = corners.map((point) => {
      const { lng, lat } = unproject(point);
      return [
        centerLongitude + ((((lng - centerLongitude + 540) % 360) + 360) % 360) - 180,
        lat,
      ] as const;
    });
    if (
      !result.every(
        ([lng, lat]) =>
          Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lat) <= 85.0511287798066,
      )
    )
      return null;
    const xs = result.map(([lng]) => lng);
    if (Math.max(...xs) - Math.min(...xs) >= 180) return null;
    return result;
  } catch {
    return null;
  }
}

/** Expand in projected space: margin is extra span on each side, relative to the full span. */
export function deriveInsetView(
  footprint: GroundFootprint,
  width: number,
  height: number,
  margin: number,
): MapView | null {
  if (
    footprint.length !== 4 ||
    ![width, height, margin].every(Number.isFinite) ||
    width <= 0 ||
    height <= 0 ||
    margin < 0 ||
    margin > 500
  )
    return null;
  if (
    !footprint.every(
      ([lng, lat]) =>
        Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lat) <= 85.0511287798066,
    )
  )
    return null;
  const points = footprint.map((coordinate) => MercatorCoordinate.fromLngLat([...coordinate]));
  const xs = points.map((p) => p.x),
    ys = points.map((p) => p.y);
  const left = Math.min(...xs),
    right = Math.max(...xs),
    top = Math.min(...ys),
    bottom = Math.max(...ys);
  const spanX = (right - left) * (1 + (2 * margin) / 100),
    spanY = (bottom - top) * (1 + (2 * margin) / 100);
  if (spanX <= 0 || spanY <= 0 || spanX > 1 || spanY > 1) return null;
  const zoom = Math.min(24, Math.log2(Math.min(width / spanX, height / spanY) / 512));
  const world = 512 * 2 ** zoom;
  const viewportSpanY = height / world;
  if (viewportSpanY > 1 || width / world > 1) return null;
  // Account for the fitted viewport, including aspect-ratio slack, before MapLibre's polar constraint.
  const centerY = Math.max(viewportSpanY / 2, Math.min(1 - viewportSpanY / 2, (top + bottom) / 2));
  const center = new MercatorCoordinate((left + right) / 2, centerY).toLngLat().wrap();
  return Number.isFinite(zoom)
    ? { center: [center.lng, center.lat], zoom, bearing: 0, pitch: 0 }
    : null;
}

export function projectInsetFootprint(
  footprint: GroundFootprint,
  view: MapView,
  width: number,
  height: number,
) {
  const center = MercatorCoordinate.fromLngLat([...view.center]);
  const world = 512 * 2 ** view.zoom;
  return footprint.map((coordinate) => {
    const longitude = coordinate[0] + 360 * Math.round((view.center[0] - coordinate[0]) / 360);
    const p = MercatorCoordinate.fromLngLat([longitude, coordinate[1]]);
    return { x: width / 2 + (p.x - center.x) * world, y: height / 2 + (p.y - center.y) * world };
  });
}
